import { TableRow } from '@sam/components';
import { Dispatch, SetStateAction } from 'react';
import { DocType, DocumentBucket, SchambeckDoc } from '../../../../shared/src/files/Files.types';
import i18n from '../../i18n/i18n';
import { downloadFile, Offer, SchambeckUser, SimpleUser } from 'shared';
import { getUserNameForSimpleUser } from '../user/User.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

/**
 * Util method to download a text file
 * @param content to download
 * @param filename name of the file to download
 */
export const downloadTextFile = (content: string, filename: string): void => {
  const blob: Blob = new Blob([content], { type: 'text/plain' });
  downloadFile(blob, filename);
};

/**
 * Util method to convert SchambeckDocs into TableEntries
 * @param docs to convert
 * @param path path to be used for delete and download action
 * @param handleDelete method to delete a file from minio and delete the corrosponding schambeckDoc
 * @param handleDownloadFile method to download a file onClick
 * @returns Array of generated TableEntries
 */
export const convertSchambeckDocsIntoTableEntries = (
  docs: SchambeckDoc[],
  handleDelete: Dispatch<
    SetStateAction<{ path: string; id: string; bucket: DocumentBucket } | undefined>
  >,
  handleDownloadFile: (
    path: string,
    contentType: string,
    fileName: string,
    bucket: DocumentBucket,
    preview: boolean,
  ) => void,
  users: SimpleUser[] | SchambeckUser[],
): TableRow[] => {
  return docs.map((doc) => ({
    id: doc.id,
    actions: [
      <IconComponent
        type={IconType.PREVIEW}
        width={30}
        onClick={(evt) => {
          evt.preventDefault();
          handleDownloadFile(doc.path + doc.name, doc.contentType, doc.name, doc.bucket, true);
        }}
        className="clickable"
      />,
      <IconComponent
        type={IconType.DELETE}
        className="clickable"
        width={30}
        onClick={(evt) => {
          evt.preventDefault();
          handleDelete({
            path: doc.path + doc.name,
            id: doc.id,
            bucket: doc.bucket,
          });
        }}
      />,
      <IconComponent
        type={IconType.DOWNLOAD}
        className="clickable"
        width={30}
        onClick={(evt) => {
          evt.preventDefault();
          handleDownloadFile(doc.path + doc.name, doc.contentType, doc.name, doc.bucket, false);
        }}
      />,
    ],
    content: [
      doc.name,
      doc.createDate.toLocaleDateString('de-DE'),
      getUserNameForSimpleUser(doc.createdBy, users),
      i18n.t(`general.docType.${doc.type}`),
    ],
  }));
};

/**
 * Util method to get the correct Doctypes for a given bucket
 * @param bucket to get the doctypes for
 * @returns  Array containing all possible doctypes
 */
export const getCorrectDocTypesForBucket = (
  bucket: DocumentBucket,
  isConfidential?: boolean,
): DocType[] => {
  switch (bucket) {
    case DocumentBucket.OFFER:
      return [DocType.REQUEST_DOCUMENT, DocType.CALCULATION, DocType.FEASIBILITY, DocType.OTHERS];
    case DocumentBucket.USER:
      if (isConfidential)
        return [
          DocType.CONTRACT_INFORMATION,
          DocType.USER_DATA,
          DocType.PERSONAL_DATA,
          DocType.CERTIFICATE,
          DocType.WRITING_TRAFFIC,
          DocType.OTHERS,
        ];
      else return [DocType.CERTIFICATE, DocType.OTHERS];
    case DocumentBucket.PROJECT:
      return [
        DocType.OFFER,
        DocType.TEST_INSTRUCTION,
        DocType.ERROR_CATALOG,
        DocType.WORK_INSTRUCTION,
        DocType.PROJECT_ORDER,
        DocType.PROTOCOL,
        DocType.OTHERS,
      ];
    case DocumentBucket.CAR:
      return [DocType.CONTRACT_INFORMATION, DocType.DAMAGE_REPORT, DocType.OTHERS];
    case DocumentBucket.BIKE:
      return [DocType.CONTRACT_INFORMATION, DocType.DAMAGE_REPORT, DocType.OTHERS];
    case DocumentBucket.INVENTORY:
      return [DocType.OTHERS];
    case DocumentBucket.INVOICE:
    case DocumentBucket.INSTRUCTION:
      return [DocType.OTHERS];
    default:
      return [];
  }
};

/**
 * Util method to get the correct path and filename for an offer document
 * @param offer the offer to get the path for
 * @returns the path including the filename
 */
export const getOfferDocumentPath = (offer: Offer): string =>
  `${offer.customerId}/${offer.orderId}/${offer.id}/${i18n.t('pages.order.create.offerFileName', {
    replace: { NUMBER: offer.numberRangeNumber },
  })}`;
